import { type ReactElement, useEffect } from 'react';
import { type AdminRuntime, screenAllowed } from '../src/index';
import { NOT_FOUND_ROUTE, PUBLIC_ROUTES, RouterProvider, useRouter } from './router';
import { AdminProvider, useAdmin } from './session';
import { ConsoleProvider } from './console';
import { I18nProvider, useT } from './i18n/provider';
import { BootSplash, Page } from '@vello/web-theme';
import { EFFECT_CLASS } from '@vello/tokens';
import { AppShell } from './components/layout';
import { PersonScreen } from './screens/PersonScreen';
import { LoginScreen } from './screens/LoginScreen';
import { RecoveryScreen } from './screens/RecoveryScreen';
import { RecoveryResetScreen } from './screens/RecoveryResetScreen';
import { SessionExpiredScreen } from './screens/SessionExpiredScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { ProvidersDashboardScreen } from './screens/ProvidersDashboardScreen';
import { CustomersDashboardScreen } from './screens/CustomersDashboardScreen';
// D522 (AN-BI-UI): Análises
import { AnalyticsPanelsScreen } from './screens/AnalyticsPanelsScreen';
import { AnalyticsExplorerScreen } from './screens/AnalyticsExplorerScreen';
import { AnalyticsReportsScreen } from './screens/AnalyticsReportsScreen';
import { AnalyticsExportsScreen } from './screens/AnalyticsExportsScreen';
// D527: the pending reports
import { AnalyticsListsScreen } from './screens/AnalyticsListsScreen';
import { AccessReportScreen } from './screens/AccessReportScreen';
import { LiveMapScreen } from './screens/LiveMapScreen';
import { RouteAnomaliesScreen } from './screens/RouteAnomaliesScreen';
import { RidesGridScreen } from './screens/RidesGridScreen';
import { ReportCatalogScreen } from './screens/ReportCatalogScreen';
import { AuditTrailScreen } from './screens/AuditTrailScreen';
import { RiskAreasScreen } from './screens/RiskAreasScreen';
import { IncentiveZonesScreen } from './screens/IncentiveZonesScreen';
import { ServiceAreasScreen } from './screens/ServiceAreasScreen';
import { DevicesScreen } from './screens/DevicesScreen';
import { TvPanelsScreen } from './screens/TvPanelsScreen';
import { PlacesScreen } from './screens/PlacesScreen';
import { CentralTeamsScreen } from './screens/CentralTeamsScreen';
import { CentralWhatsappScreen } from './screens/CentralWhatsappScreen';
import { AlertCenterScreen } from './screens/AlertCenterScreen';
import { BroadcastScreen } from './screens/BroadcastScreen';
import { BannersScreen } from './screens/BannersScreen';
import { AwardsScreen } from './screens/AwardsScreen';
import { RideChatsScreen } from './screens/RideChatsScreen';
import { RegistriesScreen } from './screens/RegistriesScreen';
import { MarketsScreen } from './screens/MarketsScreen';
import { IntegrationsScreen } from './screens/IntegrationsScreen';
import { SecurityScreen } from './screens/SecurityScreen';
// W8-OPERATORS-AND-SCOPES (D449)
import { OperatorsScreen } from './screens/OperatorsScreen';
import { AccessProfilesScreen } from './screens/AccessProfilesScreen';
import { ManualAccessScreen } from './screens/ManualAccessScreen';
import { PassengerRegistryScreen } from './screens/PassengerRegistryScreen';
import { InvitationScreen } from './screens/InvitationScreen';
import { MyAccessScreen } from './screens/MyAccessScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { OtpSetupWizardScreen } from './screens/OtpSetupWizardScreen';
import { WidgetsScreen } from './screens/WidgetsScreen';
import { LegalDocumentsScreen } from './screens/LegalDocumentsScreen';
import { CareerOpeningsScreen } from './screens/CareerOpeningsScreen';
import { FinanceScreen } from './screens/FinanceScreen';
import { CasesScreen } from './screens/CasesScreen';
import { TicketDetailScreen } from './screens/TicketDetailScreen';
import { AuditScreen } from './screens/AuditScreen';
import { CompaniesScreen } from './screens/CompaniesScreen';
import { DirectoryScreen } from './screens/DirectoryScreen';
import { CampaignsScreen } from './screens/CampaignsScreen';
import { ApprovalsScreen } from './screens/ApprovalsScreen';
import { DispatchScreen } from './screens/DispatchScreen';
import { DisputesScreen } from './screens/DisputesScreen';
import { SafetyScreen } from './screens/SafetyScreen';
import { PayoutsScreen } from './screens/PayoutsScreen';
import { PartnerBillingScreen } from './screens/PartnerBillingScreen';
import { CourierJobsScreen } from './screens/CourierJobsScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { ReportsScreen } from './screens/ReportsScreen';
import { FoodOpsScreen } from './screens/FoodOpsScreen';
import { FoodOnboardingReviewScreen } from './screens/FoodOnboardingReviewScreen';
import { FoodAdsGovernanceScreen } from './screens/FoodAdsGovernanceScreen';
import { FoodFulfillmentConfigScreen } from './screens/FoodFulfillmentConfigScreen';
import { GovernedSettingsScreen } from './screens/GovernedSettingsScreen';
import { CentralAiSettingsScreen } from './screens/CentralAiSettingsScreen';
import { AiSpendScreen } from './screens/AiSpendScreen';
import { MapsSpendScreen } from './screens/MapsSpendScreen';
import { AboutScreen } from './screens/AboutScreen';
import { RideDetailScreen } from './screens/RideDetailScreen';
import { TariffsScreen } from './screens/TariffsScreen';
import { PlaceholderScreen } from './screens/PlaceholderScreen';
// W6-ADMIN-SCREENS (D401)
import { FoodOrdersScreen } from './screens/FoodOrdersScreen';
import { FoodPostSaleScreen } from './screens/FoodPostSaleScreen';
import { StoreStatusScreen } from './screens/StoreStatusScreen';
import { SendDeliveriesScreen } from './screens/SendDeliveriesScreen';
import { SendClaimsScreen } from './screens/SendClaimsScreen';
import { MerchantStatementsScreen } from './screens/MerchantStatementsScreen';
import { RefundApprovalsScreen } from './screens/RefundApprovalsScreen';
import { CashHandoversScreen } from './screens/CashHandoversScreen';
import { DriverGroupsScreen, DriverWalletsScreen } from './screens/DriverWalletsScreen';
import { RiderWalletApprovalsScreen } from './screens/RiderWalletApprovalsScreen';
import { IncentiveFundScreen } from './screens/IncentiveFundScreen';
import { ActiveIncentivesScreen } from './screens/ActiveIncentivesScreen';
import { IncentiveCockpitScreen } from './screens/IncentiveCockpitScreen';
import { CouponCampaignsScreen } from './screens/CouponCampaignsScreen';
import { ReferralCampaignsScreen } from './screens/ReferralCampaignsScreen';
import { DriverSettlementScreen } from './screens/DriverSettlementScreen';
import { DriverDebtsScreen } from './screens/DriverDebtsScreen';
import { RiderDebtsScreen } from './screens/RiderDebtsScreen';
import { RiderCashBlocksScreen } from './screens/RiderCashBlocksScreen';
import { FourEyesInboxScreen } from './screens/FourEyesInboxScreen';
import { OpsIncidentsScreen } from './screens/OpsIncidentsScreen';
import { DynamicPricingScreen } from './screens/DynamicPricingScreen';
import { AccessDeniedScreen } from './screens/AccessDeniedScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
// W8-DRIVER-ELIGIBILITY (D437)
import { KycCatalogScreen } from './screens/KycCatalogScreen';
import { ServiceCategoriesScreen } from './screens/ServiceCategoriesScreen';
import { PlatformSetupScreen } from './screens/PlatformSetupScreen';
import { DriverDocumentsScreen } from './screens/DriverDocumentsScreen';
import { DriverRegistryScreen } from './screens/DriverRegistryScreen';
import { DriverRegisterScreen } from './screens/DriverRegisterScreen';
import { DriverDocumentQueueScreen } from './screens/DriverDocumentQueueScreen';
import { SuspensionsScreen } from './screens/SuspensionsScreen';
import { RelationshipsScreen } from './screens/RelationshipsScreen';
import { DriverVehiclesScreen } from './screens/DriverVehiclesScreen';
import { ProviderServicesQueueScreen } from './screens/ProviderServicesQueueScreen';

// Sidebar destinations whose real data lands in a later step of the platform-management program (A02 tree).
// D401 removed `tariff_dynamic` from this set: it is the real dynamic-pricing console now; D437 removed
// `driver_documents` (the assisted in-person document review).
const PLACEHOLDER_ROUTES = new Set<string>([]);

function renderScreen(route: string): ReactElement {
  switch (route) {
    case 'login':
      return <LoginScreen />;
    case 'recovery':
      return <RecoveryScreen />;
    case 'recovery_reset':
      return <RecoveryResetScreen />;
    case 'invitation':
      return <InvitationScreen />;
    case 'operators':
      return <OperatorsScreen />;
    case 'access_profiles':
      return <AccessProfilesScreen />;
    case 'manual_access':
      return <ManualAccessScreen />;
    case 'passenger_registry':
      return <PassengerRegistryScreen />;
    case 'providers_overview':
      return <ProvidersDashboardScreen />;
    case 'customers_overview':
      return <CustomersDashboardScreen />;
    case 'bi_panels':
      return <AnalyticsPanelsScreen />;
    case 'bi_explorer':
      return <AnalyticsExplorerScreen />;
    case 'bi_reports':
      return <AnalyticsReportsScreen />;
    case 'bi_exports':
      return <AnalyticsExportsScreen />;
    case 'bi_lists':
      return <AnalyticsListsScreen />;
    case 'access_report':
      return <AccessReportScreen />;
    case 'my_profile':
      return <ProfileScreen />;
    case 'my_access':
      return <MyAccessScreen />;
    case 'session_expired':
      return <SessionExpiredScreen />;
    case 'live_map':
      return <LiveMapScreen />;
    case 'ride_detail':
      return <RideDetailScreen />;
    case 'tariff_categories':
      return <TariffsScreen />;
    case 'registries':
      return <RegistriesScreen />;
    case 'markets':
      return <MarketsScreen />;
    case 'integrations':
      return <IntegrationsScreen />;
    case 'security':
      return <SecurityScreen />;
    case 'integrations_otp_setup':
      return <OtpSetupWizardScreen />;
    case 'legal_documents':
      return <LegalDocumentsScreen />;
    case 'career_openings':
      return <CareerOpeningsScreen />;
    case 'integrations_widgets':
      return <WidgetsScreen />;
    case 'finance':
      return <FinanceScreen />;
    case 'cases':
      return <CasesScreen />;
    case 'case_detail':
      return <TicketDetailScreen />;
    case 'audit':
      return <AuditScreen />;
    case 'companies':
      return <CompaniesScreen />;
    case 'passengers':
      return <DirectoryScreen realm="RIDER" titleId="dir.passengers.title" subtitleId="dir.passengers.subtitle" detailRoute="passenger_detail" />;
    case 'passenger_detail':
      return <PersonScreen realm="RIDER" listRoute="passengers" />;
    case 'driver_directory':
      return <DirectoryScreen realm="DRIVER" titleId="dir.drivers.title" subtitleId="dir.drivers.subtitle" detailRoute="driver_detail" />;
    case 'driver_detail':
      return <PersonScreen realm="DRIVER" listRoute="driver_directory" />;
    case 'campaigns':
      return <CampaignsScreen />;
    case 'driver_approvals':
      return <ApprovalsScreen />;
    case 'dispatch':
      return <DispatchScreen />;
    case 'courier_jobs':
      return <CourierJobsScreen />;
    case 'disputes':
      return <DisputesScreen />;
    case 'safety':
      return <SafetyScreen />;
    case 'finance_payouts':
      return <PayoutsScreen />;
    case 'finance_partner_billing':
      return <PartnerBillingScreen />;
    case 'notifications':
      return <NotificationsScreen />;
    case 'analytics':
      return <ReportsScreen />;
    case 'food_ops':
      return <FoodOpsScreen />;
    case 'food_review':
      return <FoodOnboardingReviewScreen />;
    case 'food_ads':
      return <FoodAdsGovernanceScreen />;
    case 'food_fulfillment':
      return <FoodFulfillmentConfigScreen />;
    // W6-ADMIN-SCREENS (D401): the consoles whose server routes already existed and had no screen.
    case 'food_orders':
      return <FoodOrdersScreen />;
    case 'food_postsale':
      return <FoodPostSaleScreen />;
    case 'food_stores':
      return <StoreStatusScreen />;
    case 'send_deliveries':
      return <SendDeliveriesScreen />;
    case 'send_claims':
      return <SendClaimsScreen />;
    case 'finance_merchant_statements':
      return <MerchantStatementsScreen />;
    case 'finance_refund_approvals':
      return <RefundApprovalsScreen />;
    case 'finance_cash_handovers':
      return <CashHandoversScreen />;
    case 'finance_driver_debts':
      return <DriverDebtsScreen />;
    case 'finance_rider_debts':
      return <RiderDebtsScreen />;
    case 'finance_rider_cash_blocks':
      return <RiderCashBlocksScreen />;
    case 'finance_wallets':
      return <DriverWalletsScreen />;
    case 'finance_rider_wallet':
      return <RiderWalletApprovalsScreen />;
    case 'finance_incentive_fund':
      return <IncentiveFundScreen />;
    case 'finance_incentive_offers':
      return <ActiveIncentivesScreen />;
    case 'finance_incentive_controller':
      return <IncentiveCockpitScreen />;
    case 'finance_coupon_campaigns':
      return <CouponCampaignsScreen />;
    case 'referrals':
      return <ReferralCampaignsScreen />;
    case 'finance_settlement':
      return <DriverSettlementScreen />;
    case 'driver_groups':
      return <DriverGroupsScreen />;
    case 'four_eyes_inbox':
      return <FourEyesInboxScreen />;
    case 'route_anomalies':
      return <RouteAnomaliesScreen />;
    // "Suporte a corridas" was merged into the grid (owner, 2026-10-18): its old address (and a person's "see every ride" link) lands on the grid.
    case 'rides':
    case 'rides_grid':
      return <RidesGridScreen />;
    case 'report_catalog':
      return <ReportCatalogScreen />;
    case 'audit_trail':
      return <AuditTrailScreen />;
    case 'risk_areas':
      return <RiskAreasScreen />;
    case 'alert_center':
      return <AlertCenterScreen />;
    case 'incentive_zones':
      return <IncentiveZonesScreen />;
    case 'service_areas':
      return <ServiceAreasScreen />;
    case 'devices':
      return <DevicesScreen />;
    case 'places': // D674
      return <PlacesScreen />;
    case 'tv_panels': // D647
      return <TvPanelsScreen />;
    case 'broadcasts':
      return <BroadcastScreen />;
    case 'banners':
      return <BannersScreen />;
    case 'awards':
      return <AwardsScreen />;
    case 'ride_chats':
      return <RideChatsScreen />;
    case 'central_teams':
      return <CentralTeamsScreen />;
    case 'central_whatsapp': // D636
      return <CentralWhatsappScreen />;
    case 'ops_incidents':
      return <OpsIncidentsScreen />;
    case 'tariff_dynamic':
      return <DynamicPricingScreen />;
    case 'governed_settings':
      return <GovernedSettingsScreen />;
    case 'central_ai_settings': // D631
      return <CentralAiSettingsScreen />;
    case 'ai_spend': // D639
      return <AiSpendScreen />;
    case 'maps_spend': // D874
      return <MapsSpendScreen />;
    case 'platform_setup': // D615 (SETUP-READINESS)
      return <PlatformSetupScreen />;
    // W8-DRIVER-ELIGIBILITY (D437)
    case 'kyc_catalog':
      return <KycCatalogScreen />;
    case 'service_categories': // D462 W9-CATEGORY-CATALOG
      return <ServiceCategoriesScreen />;
    case 'driver_documents':
      return <DriverDocumentsScreen />;
    case 'driver_vehicles':
      return <DriverVehiclesScreen />;
    case 'provider_services': // D883
      return <ProviderServicesQueueScreen />;
    // W9-DRIVER-REGISTRY (D481)
    case 'driver_registry':
      return <DriverRegistryScreen />;
    case 'driver_register':
      return <DriverRegisterScreen />;
    case 'driver_documents_all':
      return <DriverDocumentQueueScreen />;
    // W9-REPUTATION (D478)
    case 'reputation_suspensions':
      return <SuspensionsScreen />;
    case 'reputation_relationships':
      return <RelationshipsScreen />;
    case 'about':
      return <AboutScreen />;
    case 'dashboard':
      return <DashboardScreen />;
    default:
      if (PLACEHOLDER_ROUTES.has(route)) return <PlaceholderScreen route={route} />;
      // An address no screen owns is a not-found page, not the dashboard under a wrong URL (D854 L7a-020).
      return route === NOT_FOUND_ROUTE ? <NotFoundScreen /> : <DashboardScreen />;
  }
}

/** The cold-load screen: the shared `BootSplash` of `@vello/web-theme` with this app's translated label. */
function AppBoot(): ReactElement {
  const { t } = useT();
  return <BootSplash label={t('chrome.loading')} />;
}

function ScreenRouter(): ReactElement {
  const { route, navigate } = useRouter();
  const { booted, session, grants } = useAdmin();
  const isPublic = PUBLIC_ROUTES.has(route);
  const authed = session.status === 'authenticated';

  // Deny-by-default: never render an authenticated screen without a session (direct-URL protection).
  useEffect(() => {
    if (booted && !isPublic && !authed) navigate('login');
  }, [booted, isPublic, authed, navigate]);

  if (!booted) return <AppBoot />;
  if (isPublic) return renderScreen(route);
  if (!authed) return <AppBoot />;
  // D364: a screen this operator's grant does not open (a deep link, or a bookmark from another persona) says so
  // in words inside the normal console chrome, instead of rendering a screen whose first read answers 403.
  const allowed = screenAllowed(grants, route);
  return (
    <ConsoleProvider>
      <AppShell>
        {/* The content fades in when the route changes (keyed by the route; stilled under reduced motion). */}
        <Page key={route} className={`${EFFECT_CLASS.fadeIn} vello-ab-route`}>
          {allowed ? renderScreen(route) : <AccessDeniedScreen route={route} />}
        </Page>
      </AppShell>
    </ConsoleProvider>
  );
}

/**
 * Root of the Vello Admin operator console (S111–S114; M12). A real Vite + React SPA over the framework-neutral
 * `@vello/admin` logic and the `@vello/tokens` dark design system. Every color comes from a `--color-*` token.
 */
export function App({ runtime }: { runtime: AdminRuntime }): ReactElement {
  return (
    <I18nProvider>
      <RouterProvider>
        <AdminProvider runtime={runtime}>
          <ScreenRouter />
        </AdminProvider>
      </RouterProvider>
    </I18nProvider>
  );
}
